/**
 * Service API — Fonds « témoin, pas banque » (v4.52).
 * L'app ne transfère jamais d'argent : ces appels enregistrent des demandes et
 * des DÉCLARATIONS (poignée de main « j'ai envoyé » / « j'ai reçu »).
 * Les erreurs portent un `error_code` machine (contrat 5A) — voir FONDS_ERROR_MESSAGES.
 */

import api from './api.js';

/** Messages français actionnables par code machine (revue eng 5A). */
export const FONDS_ERROR_MESSAGES = {
  FONDS_DISABLED: "Le volet financier est désactivé pour le moment.",
  PHONE_VERIFICATION_REQUIRED: 'Confirmez d’abord votre numéro par SMS ou appel (bandeau en haut de la page) : un code reçu par email ne suffit pas pour les opérations d’argent.',
  CAP_EXCEEDED: 'Le plafond de la campagne serait dépassé.',
  DUPLICATE_DOCUMENT: 'Ce document a déjà été financé (ou est en cours) pour ce bénéficiaire.',
  INVALID_TRANSITION: "Cette action n'est pas possible dans l'état actuel de la demande.",
  PERTE_REQUIRED: 'Le certificat de perte est requis pour un timbre fiscal.',
  CONSENT_REQUIRED: "Le consentement du bénéficiaire doit être recueilli en personne.",
  POOL_NOT_AVAILABLE: 'Cette fonctionnalité ouvre dans une prochaine version.',
  PAYEE_RAILS_MISSING: "Le destinataire n'a pas encore attesté son numéro de paiement.",
  CONFIRM_BEFORE_SENT: "L'envoi n'a pas encore été déclaré par le payeur.",
  NOT_YOURS: 'Cette demande ne vous concerne pas.',
  DOC_INCOMPLET: 'Demande de document incomplète.',
  SECOND_SIGNER_REQUIRED: 'Un second signataire est requis pour ce document (timbre fiscal).',
  SAME_SIGNER_FORBIDDEN: 'Le second signataire doit être un compte différent du premier.',
  LEGACY_CUSTODY_GONE: 'Ce parcours de paiement a été retiré (Grand-Électeur ne transfère jamais d’argent).',
};

/**
 * Message français pour une erreur API fonds.
 * Préfère le texte serveur (souvent plus précis, ex. NOT_YOURS → « Aucun Parrain actif… ») ;
 * le dictionnaire par code sert de repli si le serveur n'envoie pas de message.
 */
export function fondsErrorMessage(err, fallback = 'Une erreur est survenue.') {
  const data = err?.response?.data;
  const serverMsg = typeof data?.error === 'string' ? data.error.trim() : '';
  if (serverMsg) return serverMsg;
  const code = data?.error_code;
  return (code && FONDS_ERROR_MESSAGES[code]) || fallback;
}

export async function createDemande(payload) {
  const res = await api.post('/fonds/demandes', payload);
  return res.data;
}

/**
 * Tarif de la carte PASTEF pour le Relais connecté, TOUJOURS en FCFA (Sénégal 1 000 /
 * Europe 13 119 / ailleurs 12 000, v4.63). Le tarif dépend de la région du RELAIS, pas
 * du bénéficiaire : un Relais annonce donc UN prix, le même pour tous ceux qu'il enrôle.
 *
 * Passe par `api` — donc authentifié : la version précédente utilisait un `fetch` nu
 * sans en-tête Authorization et n'aurait jamais répondu 200.
 *
 * TOUJOURS lire le montant ici avant de créer une demande carte_pastef : le serveur
 * refuse tout autre montant (CAP_EXCEEDED).
 *
 * @returns {Promise<{success: boolean, data?: {montant: number, devise: string, region: string}}>}
 */
export async function getPastefCost() {
  const res = await api.get('/fonds/pastef-cost');
  return res.data;
}

/**
 * Liste des demandes : `data` = tableau (toutes les demandes ouvertes + les 200
 * closes les plus récentes), `meta.truncated_closed` = historique clos partiel (F-34).
 */
export async function listDemandes(filters = {}) {
  const res = await api.get('/fonds/demandes', { params: filters });
  return res.data;
}

/**
 * Totaux d'argent du périmètre, agrégés côté serveur sur TOUTES les demandes (F-34) —
 * jamais recalculés sur la liste, qui borne l'historique clos.
 */
export async function getDemandesResume() {
  const res = await api.get('/fonds/demandes/resume');
  return res.data;
}

export async function getHistorique(demandeId) {
  const res = await api.get(`/fonds/demandes/${demandeId}/historique`);
  return res.data;
}

export async function approuver(demandeId) {
  const res = await api.post(`/fonds/demandes/${demandeId}/approuver`);
  return res.data;
}

export async function refuser(demandeId, { motif, motif_refus_audio_base64, motif_refus_audio_mime } = {}) {
  const res = await api.post(`/fonds/demandes/${demandeId}/refuser`, {
    motif, motif_refus_audio_base64, motif_refus_audio_mime,
  });
  return res.data;
}

export async function annuler(demandeId) {
  const res = await api.post(`/fonds/demandes/${demandeId}/annuler`);
  return res.data;
}

export async function getPaySheet(demandeId) {
  const res = await api.get(`/fonds/demandes/${demandeId}/pay-sheet`);
  return res.data;
}

export async function marquerEnvoye(demandeId, { channel } = {}) {
  const res = await api.post(`/fonds/demandes/${demandeId}/marquer-envoye`, { channel });
  return res.data;
}

export async function confirmerReception(demandeId) {
  const res = await api.post(`/fonds/demandes/${demandeId}/confirmer-reception`);
  return res.data;
}

export async function signalerLitige(demandeId, { commentaire } = {}) {
  const res = await api.post(`/fonds/demandes/${demandeId}/litige`, { commentaire });
  return res.data;
}

export async function createPledge(campagneId, { montant, description }) {
  const res = await api.post(`/fonds/campagnes/${campagneId}/pledges`, { montant, description });
  return res.data;
}

export async function cancelPledge(pledgeId) {
  const res = await api.post(`/fonds/pledges/${pledgeId}/annuler`);
  return res.data;
}

export async function getRegistre(campagneId) {
  const res = await api.get(`/fonds/campagnes/${campagneId}/registre`);
  return res.data;
}

export async function attesterRails(rails) {
  const res = await api.post('/fonds/attestation-rails', { rails });
  return res.data;
}

// ── Phase B : circuit pool (flux strict) ──

export async function deposerDocument(demandeId, { document_base64, document_mime }) {
  const res = await api.post(`/fonds/demandes/${demandeId}/document`, { document_base64, document_mime });
  return res.data;
}

export async function verifier(demandeId, { decision = 'verifier', motif = null } = {}) {
  const res = await api.post(`/fonds/demandes/${demandeId}/verifier`, { decision, motif });
  return res.data;
}

export async function selectionner(demandeId) {
  const res = await api.post(`/fonds/demandes/${demandeId}/selectionner`);
  return res.data;
}

export async function getCasVerifies() {
  const res = await api.get('/fonds/pool/cas-verifies');
  return res.data;
}

export async function getVerificationQueue() {
  const res = await api.get('/fonds/pool/verification-queue');
  return res.data;
}

/** F-04 : file des litiges (gouverneur validate_pool / admin), dans le périmètre du gouverneur. */
export async function getLitiges() {
  const res = await api.get('/fonds/pool/litiges');
  return res.data;
}

/**
 * F-04 : annotation gouverneur d'un litige — toujours le DOSSIER, jamais l'argent.
 * `voidDemande=true` annule la demande (libère plafond + unicité) : l'appelant
 * exige une confirmation explicite avant.
 */
export async function annoterLitige(demandeId, { note, voidDemande = false }) {
  const res = await api.post(`/fonds/demandes/${demandeId}/annoter`, { note, void: voidDemande });
  return res.data;
}

export async function getPoolStats() {
  const res = await api.get('/fonds/pool/stats');
  return res.data;
}

/**
 * Pièce (photo de présence / certificat de perte / document d'État) en Object URL.
 * Streaming authentifié — le statique public est bloqué côté serveur (charte §2).
 * Renvoie { url, mime } (le PDF ne se rend pas dans <img> — revue B).
 * L'appelant révoque l'URL (URL.revokeObjectURL) quand il n'en a plus besoin.
 *
 * Ne PAS ouvrir l'URL blob dans un nouvel onglet avec `target="_blank" rel="noopener"` :
 * Chromium affiche alors une page blanche (blob scoped au document créateur).
 */
export async function getPieceObjectUrl(demandeId, kind) {
  const res = await api.get(`/fonds/demandes/${demandeId}/piece/${kind}`, { responseType: 'blob' });
  const headerMime = String(res.headers?.['content-type'] || '').split(';')[0].trim();
  const blobMime = String(res.data?.type || '').split(';')[0].trim();
  const mime = blobMime || headerMime || 'application/octet-stream';

  // Axios + responseType blob : une erreur JSON (401/403/404) arrive aussi en Blob.
  if (mime.includes('json') || !res.data?.size) {
    let message = 'Pièce introuvable ou accès refusé.';
    try {
      const text = await res.data.text();
      const parsed = JSON.parse(text);
      if (parsed?.error) message = parsed.error;
    } catch { /* ignore */ }
    throw new Error(message);
  }

  // Revue 04/08 : une pièce est une image ou un PDF, rien d'autre. Une page
  // HTML d'erreur (proxy, hors-ligne captif) passerait le garde JSON et
  // finirait en <img> cassée muette.
  if (!mime.startsWith('image/') && !mime.includes('pdf')) {
    throw new Error('Pièce illisible (format inattendu) — réessayez.');
  }

  const typed = blobMime ? res.data : new Blob([res.data], { type: mime });
  return { url: URL.createObjectURL(typed), mime };
}
